import { useCallback, useMemo } from 'react';

import { useApi } from '@/shared/api';
import { getBearerToken } from '@/shared/api/utils';
import { AccessTokenPayload, extractTokenPayload } from '@/shared/helpers/auth';
import { useAuthStore } from '@/shared/store/auth.store';
import { useProfileStore } from '@/shared/store/profile.store';

export const useMemberAuth = () => {
  const api = useApi();
  const authStore = useAuthStore();
  const profileStore = useProfileStore();

  const isVfoMember =
    authStore.tenantOwners?.some((owner) => owner?.scopes?.includes('VFO')) ?? false;

  const loginMember = useCallback(
    async (accessToken: string) => {
      authStore.setAccessToken(accessToken);

      const jwtData = extractTokenPayload<AccessTokenPayload>(accessToken);
      const { data: me } = await api.authMe(getBearerToken(accessToken));
      if (!jwtData || !me) return;

      if (jwtData.tenantOwners) {
        authStore.setTenantOwners(jwtData.tenantOwners);
      }

      authStore.setAccount({ id: jwtData.profileId, email: me.email });
      profileStore.setFullName(me.firstName ?? '', me.lastName ?? '');
    },
    [api, authStore, profileStore],
  );

  const refreshToken = useCallback(async () => {
    const { data: authData } = await api.refreshAuthToken();

    if (!authData?.accessToken) return;

    authStore.setAccessToken(authData.accessToken);
  }, [api, authStore]);

  return useMemo(
    () => ({ loginMember, refreshToken, isVfoMember }),
    [loginMember, refreshToken, isVfoMember],
  );
};
